// The Stablecoins screen, ported from radar: headline tiles, the total of
// every fiat stablecoin (all currencies, in USD) over time, each stablecoin in
// a sortable table filtered by peg currency (a symbol opens its chain split),
// and value by chain. `StablecoinsScreen` loads the board from
// the public API on its own and refreshes every minute; `StablecoinsView`
// only arranges a board it is given. A missing figure renders as
// "unavailable" in tables and "—" in tiles, never as zero.

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";

import { NotPublished } from "../api";
import { csvFileName, downloadCsv, toCsv, type CsvColumn } from "../csv";
import { ago, compactUsd, count, signedPercent, utcTime } from "../format";
import { fetchStablecoins, type Stablecoins } from "../stablecoins";
import {
  chainRows,
  currencyOptions,
  currencyRows,
  DEFAULT_CURRENCY,
  depegBadge,
  filterByCurrency,
  historyPoints,
  unconfirmedTimes,
  minorCount,
  onchainSourceText,
  priceText,
  shareText,
  stablecoinRows,
  type ChainRow,
  type ChainSegment,
  type CurrencyFilter,
  type StablecoinRow,
} from "../stablecoins-view";
import styles from "./Stablecoins.module.css";
import { ValueChart, type ChartPreset } from "./ValueChart";

const REFRESH_MS = 60_000;
const TOP_SHOWN = 50;
const CHAINS_SHOWN = 20;

// ---- loading ----

interface Load {
  data: Stablecoins | null;
  /** The endpoint answered 404: nothing published yet. */
  pending: boolean;
  error: string | null;
}

/** Reads the board, then again every minute; a failed refresh keeps the last board. */
function useStablecoins(): Load {
  const [load, setLoad] = useState<Load>({ data: null, pending: false, error: null });
  const [, setTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const read = () =>
      fetchStablecoins(controller.signal)
        .then((data) => setLoad({ data, pending: false, error: null }))
        .catch((e: unknown) => {
          if (controller.signal.aborted) return;
          setLoad((current) => ({
            data: current.data,
            pending: e instanceof NotPublished,
            error: e instanceof NotPublished ? null : e instanceof Error ? e.message : String(e),
          }));
        });
    void read();
    const timer = setInterval(() => {
      void read();
      setTick((t) => t + 1);
    }, REFRESH_MS);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, []);

  return load;
}

// ---- small pieces ----

function Tile({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: "up" | "down" }) {
  return (
    <li className="tile">
      <span className="tile-label">{label}</span>
      <span className={`tile-value${tone ? ` ${tone}` : ""}`} data-absent={value === "—"}>
        {value}
      </span>
      {note ? <span className="tile-note">{note}</span> : null}
    </li>
  );
}

function ChangeTile({ label, value }: { label: string; value: number | null }) {
  const tone = value === null ? undefined : value > 0 ? "up" : value < 0 ? "down" : undefined;
  return <Tile label={label} note={value === null ? "no earlier reading" : "total, all currencies, vs then"} tone={tone} value={signedPercent(value)} />;
}

const absent = (text: string) => <span className={styles.absent}>{text}</span>;

function Change({ value }: { value: number | null }) {
  if (value === null) return absent("no history yet");
  return <span className={value > 0 ? "up" : value < 0 ? "down" : undefined}>{signedPercent(value)}</span>;
}

function ShareBar({ share }: { share: number | null }) {
  if (share === null) return absent("—");
  return (
    <span className={styles.share}>
      <span aria-hidden className={styles.shareTrack}>
        <span className={styles.shareFill} style={{ width: `${Math.min(100, share * 100)}%` }} />
      </span>
      {shareText(share)}
    </span>
  );
}

function MarketCap({ row }: { row: StablecoinRow }) {
  if (row.marketCap === null) return absent("not reported");
  if (!row.onchain) return <>{compactUsd(row.marketCap)}</>;
  return (
    <span title={row.onchain.title}>
      <span className={styles.onchain}>on-chain</span>
      {compactUsd(row.marketCap)}
    </span>
  );
}

function DepegBadge({ status }: { status: StablecoinRow["depeg"] }) {
  const badge = depegBadge(status);
  return (
    <span className={styles.badge} data-tone={badge.tone} title={badge.title}>
      {badge.label}
    </span>
  );
}

/** One stacked bar: the row's top chains by share, the rest as one muted segment. */
function ChainBar({ segments, chainCount }: { segments: ChainSegment[]; chainCount: number }) {
  if (!segments.length) return absent("no chain split");
  const lead = segments[0]!;
  const title = segments.map((s) => `${s.chain} ${shareText(s.share)}`).join(" · ");
  return (
    <span className={styles.chainBar} title={title}>
      <span aria-hidden className={styles.stack}>
        {segments.map((s, index) => (
          <span
            className={styles.segment}
            data-i={index === segments.length - 1 && chainCount > segments.length ? "other" : index}
            key={s.chain}
            style={{ width: `${Math.max(0.5, s.share * 100)}%` }}
          />
        ))}
      </span>
      <span className={styles.chainLead}>
        {lead.chain} {shareText(lead.share)}
        {chainCount > 1 ? <span className={styles.chainMore}> · {count(chainCount)} chains</span> : null}
      </span>
    </span>
  );
}

function ChainDistribution({ row }: { row: StablecoinRow }) {
  if (!row.chains.length) return <p className={styles.detailNote}>The API gives no chain split for this stablecoin.</p>;
  return (
    <div className={styles.detail}>
      {row.onchain ? <p className={styles.detailNote}>{row.onchain.title}</p> : null}
      {row.chains.map((c) => (
        <div className={styles.detailRow} key={c.chain}>
          <span>
            {c.chain}
            {row.onchain && c.onchain ? <span className={styles.onchain}>on-chain</span> : null}
          </span>
          <span aria-hidden className={styles.detailTrack}>
            <span className={styles.detailFill} style={{ width: `${Math.min(100, c.share * 100)}%` }} />
          </span>
          <span className={styles.detailValue}>
            {compactUsd(c.marketCap)} · {shareText(c.share)}
          </span>
        </div>
      ))}
    </div>
  );
}

function Panel({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <section aria-label={title} className={`panel ${styles.panel}`}>
      <header className="panel-head">
        <h2>{title}</h2>
        {note ? <span>{note}</span> : null}
      </header>
      {children}
    </section>
  );
}

function MoreButton({ all, total, shown, onClick, noun }: { all: boolean; total: number; shown: number; onClick: () => void; noun: string }) {
  if (total <= shown) return null;
  return (
    <button className={styles.more} onClick={onClick} type="button">
      {all ? `Show the top ${shown}` : `Show all ${count(total)} ${noun}`}
    </button>
  );
}

// ---- sections ----

function Tiles({ data }: { data: Stablecoins }) {
  const { totals } = data;
  const minor = minorCount(data);
  return (
    <ul className="tiles">
      <Tile label="Stablecoin market cap" note="every fiat peg, valued in USD" value={compactUsd(totals.market_cap)} />
      <CurrencyTile data={data} />
      <ChangeTile label="Change · 24h" value={totals.change_24h} />
      <ChangeTile label="Change · 7d" value={totals.change_7d} />
      <ChangeTile label="Change · 30d" value={totals.change_30d} />
      <Tile label="Stablecoins" note={`tracked across ${count(data.chains.length)} chains`} value={count(totals.stablecoins)} />
      <Tile
        label="Depegged"
        note={totals.depegged ? `${minor ? `${count(minor)} more drifting · ` : ""}price broke its peg` : minor ? `none broken · ${count(minor)} drifting` : "every price on its peg"}
        tone={totals.depegged ? "down" : undefined}
        value={count(totals.depegged)}
      />
    </ul>
  );
}

/** The total by peg currency: the largest one's share, then the next few in USD. */
function CurrencyTile({ data }: { data: Stablecoins }) {
  const rows = useMemo(() => currencyRows(data), [data]);
  const [first, ...rest] = rows;
  if (!first) return null;
  const NEXT = 3;
  const next = rest.slice(0, NEXT).map((c) => `${c.currency} ${c.marketCap === null ? "—" : compactUsd(c.marketCap)}`);
  const more = rest.length - NEXT;
  return (
    <Tile
      label={`By currency · ${count(rows.length)}`}
      note={next.length ? `${next.join(" · ")}${more > 0 ? ` · ${count(more)} more` : ""}` : "one peg currency"}
      value={`${first.currency} ${first.share === null ? "—" : shareText(first.share)}`}
    />
  );
}

/** The stablecoin presets: its history is long, so the window starts at a year. */
const STABLECOIN_PRESETS: readonly ChartPreset[] = [
  { id: "30d", label: "30D", days: 30 },
  { id: "90d", label: "90D", days: 90 },
  { id: "1y", label: "1Y", days: 365 },
  { id: "all", label: "All", days: null },
];

function History({ data }: { data: Stablecoins }) {
  const points = useMemo(() => historyPoints(data), [data]);
  const details = useMemo(() => {
    const times = unconfirmedTimes(data);
    return times.size ? new Map([...times].map((t) => [t, { unconfirmed: true }])) : undefined;
  }, [data]);
  return (
    <Panel title="Total market cap over time">
      <ValueChart defaultPreset="1y" details={details} exportName="stablecoin-market-cap" format={compactUsd} points={points} presets={STABLECOIN_PRESETS} title="Stablecoin market cap, all currencies, in USD" />
    </Panel>
  );
}

type ColumnId = "symbol" | "mechanism" | "price" | "mcap" | "share" | "change24h" | "change7d" | "change30d" | "depeg" | "chains";

interface ColumnSpec {
  id: ColumnId;
  label: string;
  num?: boolean;
  /** Sort value; undefined sorts last either way. */
  value: (row: StablecoinRow) => number | string | undefined;
  cell: (row: StablecoinRow) => ReactNode;
  text?: boolean;
}

const COLUMNS: ColumnSpec[] = [
  { id: "symbol", label: "Stablecoin", text: true, value: (r) => r.symbol.toLowerCase(), cell: () => null },
  { id: "mechanism", label: "Mechanism", text: true, value: (r) => r.mechanism?.toLowerCase(), cell: (r) => r.mechanism ?? absent("—") },
  { id: "price", label: "Price", num: true, value: (r) => r.price ?? undefined, cell: (r) => (r.price === null ? absent("no price") : priceText(r.price)) },
  { id: "mcap", label: "Market cap", num: true, value: (r) => r.marketCap ?? undefined, cell: (r) => <MarketCap row={r} /> },
  { id: "share", label: "Share", value: (r) => r.share ?? undefined, cell: (r) => <ShareBar share={r.share} /> },
  { id: "change24h", label: "24h", num: true, value: (r) => r.change24h ?? undefined, cell: (r) => <Change value={r.change24h} /> },
  { id: "change7d", label: "7d", num: true, value: (r) => r.change7d ?? undefined, cell: (r) => <Change value={r.change7d} /> },
  { id: "change30d", label: "30d", num: true, value: (r) => r.change30d ?? undefined, cell: (r) => <Change value={r.change30d} /> },
  { id: "depeg", label: "Peg", value: (r) => r.depegRank ?? undefined, cell: (r) => <DepegBadge status={r.depeg} /> },
  { id: "chains", label: "Chains", value: (r) => (r.chainCount ? r.chainCount : undefined), cell: (r) => <ChainBar chainCount={r.chainCount} segments={r.segments} /> },
];

/** CSV columns per table column, in table order. */
const STABLECOIN_CSV: Record<ColumnId, Array<CsvColumn<StablecoinRow>>> = {
  symbol: [
    { header: "Symbol", value: (r) => r.symbol },
    { header: "Name", value: (r) => r.name },
    { header: "Currency", value: (r) => r.currency },
  ],
  mechanism: [{ header: "Mechanism", value: (r) => r.mechanism }],
  price: [{ header: "Price (USD)", value: (r) => r.price }],
  mcap: [
    { header: "Market cap (USD)", value: (r) => r.marketCap },
    { header: "Source", value: (r) => (r.onchain ? "onchain" : "defillama") },
    { header: "DefiLlama market cap (USD)", value: (r) => r.defillamaCap },
  ],
  share: [{ header: "Share of total (fraction)", value: (r) => r.share }],
  change24h: [{ header: "24h change (fraction)", value: (r) => r.change24h }],
  change7d: [{ header: "7d change (fraction)", value: (r) => r.change7d }],
  change30d: [{ header: "30d change (fraction)", value: (r) => r.change30d }],
  depeg: [{ header: "Peg status", value: (r) => r.depeg }],
  chains: [
    { header: "Chains", value: (r) => r.chainCount || null },
    { header: "Largest chain", value: (r) => r.chains[0]?.chain ?? null },
  ],
};

function StablecoinTable({ data }: { data: Stablecoins }) {
  const rows = useMemo(() => stablecoinRows(data), [data]);
  const options = useMemo(() => currencyOptions(currencyRows(data), rows.length), [data, rows.length]);
  const [currency, setCurrency] = useState<CurrencyFilter>(DEFAULT_CURRENCY);
  // USD by default; every currency when the board has no USD coin.
  const shownCurrency = options.some((o) => o.value === currency) ? currency : "all";
  const inCurrency = useMemo(() => filterByCurrency(rows, shownCurrency), [rows, shownCurrency]);
  const [query, setQuery] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "mcap", desc: true }]);
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? inCurrency.filter((r) => r.symbol.toLowerCase().includes(needle) || r.name.toLowerCase().includes(needle)) : inCurrency;
  }, [inCurrency, query]);

  const columns = useMemo<Array<ColumnDef<StablecoinRow>>>(
    () =>
      COLUMNS.map((spec) => ({
        id: spec.id,
        accessorFn: spec.value,
        header: spec.label,
        sortUndefined: "last",
        sortDescFirst: !spec.text,
        cell: (context) => {
          const row = context.row.original;
          if (spec.id !== "symbol") return spec.cell(row);
          const expanded = open === row.id;
          return (
            <>
              <button
                aria-controls={expanded ? `stablecoin-chains-${row.id}` : undefined}
                aria-expanded={expanded}
                className={styles.coin}
                onClick={() => setOpen(expanded ? null : row.id)}
                title="Show chain distribution"
                type="button"
              >
                {row.symbol || row.name}
              </button>
              {row.held ? (
                <span className={styles.held} title={row.held.title}>
                  {row.held.label}
                </span>
              ) : null}
              {row.currency !== "USD" ? (
                <span className={styles.peg} title={`Pegged to ${row.currency}; prices and caps in USD`}>
                  {row.currency}
                </span>
              ) : null}
              <span className={styles.coinName}>{row.name}</span>
            </>
          );
        },
      })),
    [open],
  );

  const table = useReactTable({
    data: matches,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const sorted = table.getRowModel().rows;

  /** Every row the currency filter and search leave, in the table's order (the top-50 cut is paging, not a filter). */
  function exportCsv(): void {
    const name = `stablecoins-${shownCurrency === "all" ? "all-currencies" : shownCurrency.toLowerCase()}`;
    downloadCsv(csvFileName(name, data.as_of), toCsv(sorted.map((row) => row.original), COLUMNS.flatMap((c) => STABLECOIN_CSV[c.id])));
  }
  const shown = all ? sorted : sorted.slice(0, TOP_SHOWN);

  return (
    <Panel
      note={
        query.trim()
          ? `${count(matches.length)} of ${count(inCurrency.length)} match`
          : `${count(inCurrency.length)} ${shownCurrency === "all" ? "" : `${shownCurrency} `}stablecoins · click a symbol for its chains`
      }
      title="Stablecoins by market cap"
    >
      <div className={styles.toolbar}>
        <input
          aria-label="Search stablecoins"
          className={styles.search}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name or symbol"
          type="search"
          value={query}
        />
        <button className={styles.csv} disabled={!sorted.length} onClick={exportCsv} type="button">
          Download CSV
        </button>
        <select aria-label="Peg currency" className={styles.currency} onChange={(e) => setCurrency(e.target.value)} value={shownCurrency}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div aria-label="Stablecoin data" className={styles.scroll} role="region" tabIndex={0}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => {
                  const spec = COLUMNS.find((c) => c.id === header.column.id)!;
                  const sorted = header.column.getIsSorted();
                  return (
                    <th
                      aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none"}
                      data-num={spec.num}
                      key={header.id}
                      scope="col"
                    >
                      <button className={styles.sort} data-sorted={Boolean(sorted)} onClick={header.column.getToggleSortingHandler()} title={`Sort by ${spec.label}`} type="button">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <span aria-hidden className={styles.sortMark}>
                          {sorted === "desc" ? "↓" : sorted === "asc" ? "↑" : "↕"}
                        </span>
                      </button>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {shown.map((row) => {
              const expanded = open === row.original.id;
              return (
                <Fragment key={row.id}>
                  <tr data-depeg={row.original.depeg}>
                    {row.getVisibleCells().map((cell) => {
                      const spec = COLUMNS.find((c) => c.id === cell.column.id)!;
                      return (
                        <td data-col={spec.id} data-num={spec.num} key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                  {expanded ? (
                    <tr className={styles.detailTr} id={`stablecoin-chains-${row.original.id}`}>
                      <td colSpan={COLUMNS.length}>
                        <ChainDistribution row={row.original} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {shown.length === 0 ? <p className="panel-empty">{query.trim() ? "No stablecoin matches that search." : "No stablecoin pegged to that currency."}</p> : null}
      </div>
      <MoreButton all={all} noun="stablecoins" onClick={() => setAll(!all)} shown={TOP_SHOWN} total={sorted.length} />
    </Panel>
  );
}

function ChainsTable({ data }: { data: Stablecoins }) {
  const rows: ChainRow[] = useMemo(() => chainRows(data), [data]);
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, CHAINS_SHOWN);
  return (
    <Panel note="value by chain, all currencies, in USD" title="Stablecoins by chain">
      {rows.length === 0 ? (
        <p className="panel-empty">No chain totals published yet.</p>
      ) : (
        <>
          <div aria-label="Chain data" className={styles.scroll} role="region" tabIndex={0}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Chain</th>
                  <th data-num scope="col">
                    Market cap
                  </th>
                  <th scope="col">Share</th>
                  <th data-num scope="col">
                    Stablecoins
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row, index) => (
                  <tr key={row.chain}>
                    <td className={styles.rank}>{row.marketCap === null ? "" : index + 1}</td>
                    <td className={styles.strong}>{row.chain}</td>
                    <td data-num>{row.marketCap === null ? absent("not reported") : compactUsd(row.marketCap)}</td>
                    <td>
                      <ShareBar share={row.share} />
                    </td>
                    <td data-num>{row.stablecoins === null ? absent("—") : count(row.stablecoins)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <MoreButton all={all} noun="chains" onClick={() => setAll(!all)} shown={CHAINS_SHOWN} total={rows.length} />
        </>
      )}
    </Panel>
  );
}

// ---- the screen ----

/** Arranges a board it is given; `StablecoinsScreen` loads one. */
export function StablecoinsView({ data }: { data: Stablecoins }) {
  const onchain = onchainSourceText(data.totals);
  return (
    <div className={styles.screen}>
      <p className="venue-meta">
        Source: {onchain ? `${onchain}, ` : null}
        <a className={styles.sourceLink} href="https://defillama.com/stablecoins" rel="noreferrer" target="_blank">
          DefiLlama
        </a>
        {onchain ? " for the rest" : null} · as of <strong>{utcTime(data.as_of)}</strong> · updated {ago(data.generated_at)} · refreshes every 30 min
      </p>
      <Tiles data={data} />
      <History data={data} />
      <StablecoinTable data={data} />
      <ChainsTable data={data} />
      <p className={styles.footnote}>
        Market cap is each stablecoin&apos;s circulating supply, valued in USD. For the coins marked on-chain, Panther reads
        the supply from the issuers&apos; contracts on their main chains (less tokens the issuer holds unissued) and takes
        DefiLlama&apos;s figure for any other chain; the coin&apos;s chain split shows DefiLlama&apos;s own total beside ours.
        Every other coin is as DefiLlama reports it. Every fiat peg is
        included (USD, EUR, JPY and more), each labelled by its currency; prices are in USD, so a euro coin on its peg
        reads about the EUR rate. Totals, changes and shares cover all currencies. A missing reading shows as &ldquo;—&rdquo; or a
        short reason, never as zero.
      </p>
    </div>
  );
}

/** The Stablecoins screen: loads the public board itself and refreshes every minute. */
export function StablecoinsScreen() {
  const { data, pending, error } = useStablecoins();
  if (data) return <StablecoinsView data={data} />;
  return (
    <div className={styles.screen}>
      {pending ? (
        <div className={styles.notice} role="status">
          <p className={styles.noticeTitle}>Stablecoin data is not published yet.</p>
          <p>The stablecoin collector has not finished its first run. This screen fills in on its own once the API publishes a board; no reload needed.</p>
        </div>
      ) : error ? (
        <p className="venue-meta">
          <span className="error">Data unavailable right now: {error}</span>
        </p>
      ) : (
        <p className="venue-meta">Loading…</p>
      )}
    </div>
  );
}
